import { motion } from 'framer-motion'
import { Users, Video, MessageSquare, Calendar, Award, Target, ArrowRight } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'

export default function MentorshipPage() {
    const benefits = [
        {
            icon: Video,
            title: 'One-on-One Sessions',
            description: 'Personal video calls with expert mentors who are NEET toppers',
        },
        {
            icon: MessageSquare,
            title: 'Instant Doubt Clearing',
            description: 'Get your doubts resolved in real-time through in-app chat',
        },
        {
            icon: Calendar,
            title: 'Personalized Study Plans',
            description: 'Customized preparation roadmap based on your strengths and weaknesses',
        },
        {
            icon: Award,
            title: 'Performance Tracking',
            description: 'Regular assessments and detailed progress reports from your mentor',
        },
        {
            icon: Target,
            title: 'Goal Setting & Monitoring',
            description: 'Set realistic goals and track your journey with mentor guidance',
        },
        {
            icon: Users,
            title: 'Peer Learning Groups',
            description: 'Join batch groups for collaborative learning and motivation',
        },
    ]

    const mentors = [
        {
            name: 'Ram Charan',
            qualification: 'AIIMS Delhi, AIR 23',
            experience: '8 years',
            students: 500,
            rating: 4.9,
        },
        {
            name: 'Arunkumar',
            qualification: 'JIPMER, AIR 45',
            experience: '6 years',
            students: 450,
            rating: 4.8,
        },
        {
            name: 'Karthik',
            qualification: 'MAMC Delhi, AIR 67',
            experience: '7 years',
            students: 480,
            rating: 4.9,
        },
    ]

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-32 pb-24 overflow-hidden">


                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <span className="inline-block px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30 mb-6">
                            Expert Mentorship
                        </span>
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6">
                            <span className="text-theme-primary">Learn from the</span>
                            <span className="block mt-2 gradient-text">Best NEET Toppers</span>
                        </h1>
                        <p className="text-xl text-theme-secondary mb-8">
                            Get personalized guidance from doctors who have cracked NEET with top ranks
                        </p>
                        <button className="px-8 py-4 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg glow-primary transition-all duration-300 inline-flex items-center gap-2">
                            Find Your Mentor
                            <ArrowRight className="h-5 w-5" />
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                            Mentorship <span className="gradient-text">Benefits</span>
                        </h2>
                        <p className="text-lg text-theme-secondary max-w-2xl mx-auto">
                            Everything you need for guided NEET preparation
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {benefits.map((benefit, index) => {
                            const Icon = benefit.icon
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <GlassmorphicCard className="p-6 h-full hover:scale-105 transition-all duration-300 group">
                                        <div className="p-3 rounded-xl bg-primary-blue/20 text-primary-blue w-fit mb-4 group-hover:bg-primary-blue group-hover:text-white transition-all">
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <h3 className="text-xl font-display font-semibold text-theme-primary mb-3">
                                            {benefit.title}
                                        </h3>
                                        <p className="text-theme-muted leading-relaxed">
                                            {benefit.description}
                                        </p>
                                    </GlassmorphicCard>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* Featured Mentors */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-theme-primary mb-4">
                            Meet Our <span className="gradient-text">Top Mentors</span>
                        </h2>
                        <p className="text-lg text-theme-secondary">
                            Learn from doctors who have been in your shoes
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {mentors.map((mentor, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <GlassmorphicCard className="p-6 text-center hover:scale-105 transition-all duration-300">
                                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-blue to-accent-gold p-1 mx-auto mb-4">
                                        <div className="w-full h-full rounded-full bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                                            <span className="text-3xl font-bold gradient-text">
                                                {mentor.name.charAt(0)}
                                            </span>
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-display font-semibold text-theme-primary mb-2">
                                        {mentor.name}
                                    </h3>
                                    <p className="text-sm text-primary-blue mb-3">{mentor.qualification}</p>
                                    <div className="space-y-2 text-sm text-theme-muted">
                                        <p>{mentor.experience} teaching experience</p>
                                        <p>{mentor.students}+ students mentored</p>
                                        <div className="flex items-center justify-center gap-1 text-accent-gold">
                                            <Award className="h-4 w-4" />
                                            <span>{mentor.rating} rating</span>
                                        </div>
                                    </div>
                                </GlassmorphicCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <GlassmorphicCard variant="elevated" className="p-12 text-center max-w-4xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-display font-bold text-theme-primary mb-4">
                            Ready to start your mentorship journey?
                        </h2>
                        <p className="text-lg text-theme-secondary mb-8">
                            Book a free consultation call with our counselors to find the perfect mentor for you
                        </p>
                        <button className="px-8 py-4 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg glow-primary transition-all duration-300 inline-flex items-center gap-2">
                            Book Free Consultation
                            <ArrowRight className="h-5 w-5" />
                        </button>
                    </GlassmorphicCard>
                </div>
            </section>
        </div>
    )
}
